import React from 'react'
import { Dialog } from '@mui/material'
import { CloseRounded, ContentCopyRounded, UploadFileOutlined, RestartAltRounded } from '@mui/icons-material'
import s from './SystemV2.module.css'

/*
 * Upload a picture for an icon — the tab icons' and the source icons' form.
 * Left: the icon in use now, with a Copy button (the clipboard takes it as a
 * PNG). Right: a drop area that also takes a click or a paste anywhere in the
 * form — a picture, a data:image URL, or (when `svg` is on) SVG markup — and,
 * once there is one, the new icon previewed the way the caller draws it.
 * Save hands the data: URL back; Reset to default shows only when the caller
 * has a custom icon to drop.
 */

/* "data:image/svg+xml;base64,…" from SVG markup — btoa needs bytes, not UTF-16 */
const svgDataUrl = (svg: string) => `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg.trim())))}`

/* an icon as a PNG blob — the clipboard takes PNG everywhere, ICO and SVG nowhere */
async function pngBlob(src: string): Promise<Blob> {
  const img = new Image()
  img.src = src
  await img.decode()
  const size = Math.max(64, img.naturalWidth || 0, img.naturalHeight || 0)
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no canvas')
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(img, 0, 0, size, size)
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('no png'))), 'image/png'))
}

export function IconUploadDialog({ title, current, currentSrc, custom, types, accept, maxBytes, rules, svg = false, preview, busy, onSave, onReset, onClose }: {
  title: string
  /** the icon in use now, drawn the way the caller draws it */
  current: React.ReactNode
  /** its picture, when it is one — what Copy puts on the clipboard */
  currentSrc?: string | null
  custom: boolean
  /** the picture types taken, as the MIME subtype after image/ */
  types: string[]
  accept: string
  maxBytes: number
  /** one line under the drop area: types, size, shape */
  rules: string
  /** take pasted SVG markup too */
  svg?: boolean
  /** the new picture drawn the way the caller will draw it */
  preview: (src: string) => React.ReactNode
  busy: boolean
  onSave: (dataUrl: string) => void
  onReset?: () => void
  onClose: () => void
}) {
  const [next, setNext] = React.useState<string | null>(null)
  const [err, setErr] = React.useState<string | null>(null)
  const [over, setOver] = React.useState(false)
  const [copied, setCopied] = React.useState<'ok' | 'err' | null>(null)
  const fileRef = React.useRef<HTMLInputElement>(null)
  const typeRe = new RegExp(`^image/(${types.map((t) => t.replace(/[.+]/g, '\\$&')).join('|')})$`)
  const urlRe = new RegExp(`^data:image/(${types.map((t) => t.replace(/[.+]/g, '\\$&')).join('|')});base64,`)
  const typeNames = types.map((t) => (t === 'x-icon' || t === 'vnd.microsoft.icon' ? 'ICO' : t === 'svg+xml' ? 'SVG' : t.toUpperCase()))
  const kb = Math.round(maxBytes / 1024)

  function readFile(file?: File | null) {
    if (!file) return
    if (!typeRe.test(file.type)) return setErr(`The picture must be ${[...new Set(typeNames)].join(', ')}`)
    if (file.size > maxBytes) return setErr(`The picture must be ${kb} KB or smaller`)
    const reader = new FileReader()
    reader.onload = () => { setErr(null); setNext(String(reader.result)) }
    reader.readAsDataURL(file)
  }

  function readText(text: string) {
    const t = text.trim()
    if (urlRe.test(t)) { setErr(null); setNext(t); return }
    if (svg && /^<svg[\s>][\s\S]*<\/svg>$/.test(t)) { setErr(null); setNext(svgDataUrl(t)); return }
    setErr(svg ? 'Paste a picture, an SVG or a data:image URL' : 'Paste a picture or a data:image URL')
  }

  // a paste anywhere in the open form counts — no need to click the drop area first
  React.useEffect(() => {
    const onPaste = (ev: Event) => {
      const e = ev as ClipboardEvent
      const items = Array.from(e.clipboardData?.items || [])
      const pic = items.find((i) => i.kind === 'file' && i.type.startsWith('image/'))
      if (pic) { e.preventDefault(); readFile(pic.getAsFile()); return }
      const text = e.clipboardData?.getData('text/plain')
      const target = e.target as HTMLElement | null
      if (text && !(target && /^(INPUT|TEXTAREA)$/.test(target.tagName))) { e.preventDefault(); readText(text) }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  })

  async function copy() {
    if (!currentSrc) return
    try {
      const blob = await pngBlob(currentSrc)
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
      setCopied('ok')
    } catch {
      setCopied('err')
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} disablePortal maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
      <div className={s.importShell}>
        <div className={s.importTop}>
          <b>{title}</b>
          <button type="button" className={s.setReset} onClick={onClose} disabled={busy} aria-label="Close"><CloseRounded sx={{ fontSize: 18 }} /></button>
        </div>
        <div className={s.importBody}>
          <div className={s.tiPair}>
            <div className={s.tiSide}>
              <span className={s.tiCaption}>Now {custom ? '· changed' : '· default'}</span>
              {current}
              {currentSrc && (
                <button type="button" className={s.ghostBtn} onClick={() => void copy()}>
                  <ContentCopyRounded sx={{ fontSize: 15 }} />{copied === 'ok' ? 'Copied' : 'Copy icon'}
                </button>
              )}
              {copied === 'err' && <span className={s.err}>This browser would not copy a picture — right-click the icon and copy it instead.</span>}
            </div>

            <div className={s.tiSide}>
              <span className={s.tiCaption}>New</span>
              {next ? (
                <>
                  {preview(next)}
                  <button type="button" className={s.ghostBtn} onClick={() => setNext(null)}>Choose another</button>
                </>
              ) : (
                <div
                  className={`${s.drop} ${s.tiDrop} ${over ? s.dropOver : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setOver(true) }}
                  onDragLeave={() => setOver(false)}
                  onDrop={(e) => {
                    e.preventDefault(); setOver(false)
                    const f = e.dataTransfer.files?.[0]
                    if (f) readFile(f)
                    else readText(e.dataTransfer.getData('text/plain') || '')
                  }}
                >
                  <button type="button" className={s.dropBtn} onClick={() => fileRef.current?.click()}>
                    <UploadFileOutlined sx={{ fontSize: 26 }} />
                    <b>Drop the picture here</b>
                    <span>or click to choose a file, or paste it (⌘V / Ctrl+V)</span>
                    <span>{rules}</span>
                  </button>
                  <input ref={fileRef} type="file" hidden accept={accept} onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = '' }} />
                </div>
              )}
            </div>
          </div>

          {err && <span className={s.err}>{err}</span>}

          <div className={s.editRow}>
            {custom && onReset && (
              <button type="button" className={s.ghostBtn} onClick={onReset} disabled={busy}>
                <RestartAltRounded sx={{ fontSize: 16 }} />Reset to default
              </button>
            )}
            <span className={s.spacer} />
            <button type="button" className={s.ghostBtn} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className={s.primary} onClick={() => next && onSave(next)} disabled={!next || busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
